import type { OrganizerNotificationsQuery } from "../graphql/notification/notification.hooks";

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  useOrganizerNotificationsQuery,
  useReadOrganizerNotificationMutation,
  useRemoveOrganizerNotificationMutation,
} from "../graphql/notification/notification.hooks";

type NotificationItem =
  OrganizerNotificationsQuery["organizerNotifications"]["items"][number];

export const useNotifications = (limit = 20) => {
  const { locale: localeParam } = useParams();
  const locale = localeParam ?? "en";
  const [page, setPage] = useState(1);
  const [allItems, setAllItems] = useState<NotificationItem[]>([]);
  const pageRef = useRef(page);
  pageRef.current = page;
  const isMounted = useRef(false);

  const { data, loading, refetch } = useOrganizerNotificationsQuery({
    variables: { page, limit },
    fetchPolicy: "no-cache",
  });
  const [removeNotification] = useRemoveOrganizerNotificationMutation();
  const [readNotification] = useReadOrganizerNotificationMutation();

  const markAllAsRead = useCallback(async () => {
    const unread = allItems.filter((item) => !item.wasRead);
    if (!unread.length) return;
    setAllItems((prev) => prev.map((item) => ({ ...item, wasRead: true })));
    await Promise.allSettled(
      unread.map((item) => readNotification({ variables: { id: item.id } }))
    );
    await refetch();
  }, [allItems, readNotification, refetch]);

  const markAsRead = useCallback(
    async (id: number) => {
      setAllItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, wasRead: true } : item))
      );
      try {
        await readNotification({ variables: { id } });
        await refetch();
      } catch {
        setAllItems((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, wasRead: false } : item
          )
        );
      }
    },
    [readNotification, refetch]
  );

  const handleRemove = async (id: number) => {
    setAllItems((prev) => prev.filter((item) => item.id !== id));
    try {
      await removeNotification({ variables: { id } });
    } catch {
      await refetch();
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
    setAllItems([]);
    refetch({ page: 1, limit });
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const items = data?.organizerNotifications?.items;
    if (!items) return;
    setAllItems((prev) => {
      const merged = pageRef.current === 1 ? items : [...prev, ...items];
      return [...merged].sort((a, b) => Number(a.wasRead) - Number(b.wasRead));
    });
  }, [data]);

  const pagination = data?.organizerNotifications;

  const loadMore = useCallback(() => {
    if (!loading && pagination && page < pagination.maxPage) {
      setPage((p) => p + 1);
    }
  }, [loading, pagination, page]);

  return {
    notifications: allItems,
    unreadCount: pagination?.unreadCount ?? 0,
    hasMore: page < (pagination?.maxPage ?? 1),
    loading,
    loadMore,
    handleRemove,
    markAllAsRead,
    markAsRead,
  };
};
